/**
 * Resolved part prices for the work-order screens (#3034, T13b2).
 *
 * A catalog part picked onto a work order is priced by the server's one resolver
 * (`POST /inventory/part-prices/resolve`, booking context): the cheaper of the customer price and an
 * active campaign, never stacked, else the category margin or the catalog price. Nothing here evaluates a rule, and the
 * catalog price of the part list is never a fallback: a pick whose price could not be resolved is a
 * `PartPriceResolutionFailure` the screen shows, not a quiet catalog price.
 *
 * Contract notes
 *  - The resolved `unitNetCents` is a NET price (four decimals at most). The service-detail rows store whole GROSS cents:
 *    `resolvedUnitGrossCents` converts once, with the shared `netToGrossCents`, at the VAT rate of the row that receives it.
 *  - One call is all or nothing. Part ids are de-duplicated and sent in requests of at most `MAX_RESOLVE_PARTS` (the
 *    endpoint's own limit); a failure of any request, or an answer that does not hold exactly one usable price per part,
 *    fails the whole call and returns no partial map.
 *  - The call belongs to the session (tenant, user, session family, generation) and the permissions of the render that
 *    offered it, and needs `inventory.read` + `work_orders.read` (the booking context of the endpoint). A call that lacks a
 *    permission NOW is refused before anything is sent (`forbidden`); an answer that outlived its session or a permission is
 *    never returned (`LateResultError`, see `isSessionCancel`).
 *  - A request that does not answer within `RESOLVE_TIMEOUT_MS` fails (`timeout`), so an unanswered pick never holds a row
 *    forever; its late answer is dropped.
 */

import { useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { netToGrossCents } from '@/utils/money';
import {
  PRICING_READ_PERMISSION,
  PricingAccessError,
  usePricingAccess,
} from '@/features/inventory/pricing/pricingSession';
import {
  resolvePartPrices,
  runOwned,
  type PartPriceSource,
  type ResolvedPartPrice,
  type ResolvePartPricesResponse,
} from './usePartPricing';

export const WORK_ORDER_READ_PERMISSION = 'work_orders.read';
/** What the booking context of the resolve endpoint needs (`inventory.read` and `work_orders.read`). */
export const PART_RESOLVE_PERMISSIONS: readonly string[] = [PRICING_READ_PERMISSION, WORK_ORDER_READ_PERMISSION];

/** The endpoint's own bound on `partIds` (`MAX_RESOLVE_PARTS` of the staff-api schema). */
export const MAX_RESOLVE_PARTS_PER_REQUEST = 500;
export const RESOLVE_TIMEOUT_MS = 20_000;

export type PartPriceFailureReason =
  | 'forbidden'
  | 'part_not_found'
  | 'unavailable'
  | 'timeout'
  | 'invalid_response'
  | 'request_failed';

export class PartPriceResolutionFailure extends Error {
  constructor(
    readonly reason: PartPriceFailureReason,
    readonly partIds: readonly string[] = [],
  ) {
    super(`Part price resolution failed: ${reason}`);
    this.name = 'PartPriceResolutionFailure';
  }
}

export type ResolvedPartSource = Exclude<PartPriceSource, 'manual'>;

/** What the line keeps of a resolution: the net price and where it came from. No rule, party or candidate detail. */
export interface ResolvedPartLinePrice {
  partId: string;
  /** Net cents, up to four decimals. */
  unitNetCents: number;
  source: ResolvedPartSource;
}

const RESOLVED_SOURCES: readonly ResolvedPartSource[] = ['catalog', 'category_margin', 'customer_price', 'campaign'];

/** `labelKey`s (workorders namespace) of the price sources, FI and EN. `manual` is a user override, `unknown` a legacy line. */
export const PART_PRICE_SOURCE_LABEL_KEYS: Record<PartPriceSource | 'unknown', string> = {
  catalog: 'detail.partPricing.source.catalog',
  category_margin: 'detail.partPricing.source.category_margin',
  customer_price: 'detail.partPricing.source.customer_price',
  campaign: 'detail.partPricing.source.campaign',
  manual: 'detail.partPricing.source.manual',
  unknown: 'detail.partPricing.source.unknown',
};

export function partPriceSourceLabelKey(source: PartPriceSource | null | undefined): string {
  return source != null && source in PART_PRICE_SOURCE_LABEL_KEYS
    ? PART_PRICE_SOURCE_LABEL_KEYS[source]
    : PART_PRICE_SOURCE_LABEL_KEYS.unknown;
}

export function partPriceFailureLabelKey(reason: PartPriceFailureReason): string {
  return `detail.partPricing.failed.${reason}`;
}

/** The failure of a thrown value, or null when it is not one of this module's (a session cancel, a programming error). */
export function asPartPriceFailure(error: unknown): PartPriceResolutionFailure | null {
  return error instanceof PartPriceResolutionFailure ? error : null;
}

/**
 * Whole gross cents of a resolved NET price at a row's VAT rate, with the shared helper. The result is what a row stores as
 * `unitPriceGrossCents`; it is never derived from a catalog gross/net price and never rounded by hand.
 */
export function resolvedUnitGrossCents(unitNetCents: number, vatPercent: number): number {
  if (!Number.isFinite(unitNetCents) || unitNetCents < 0) throw new RangeError('A resolved net price must be a non-negative number');
  if (!Number.isFinite(vatPercent) || vatPercent < 0) throw new RangeError('A VAT rate must be a non-negative number');
  return netToGrossCents(unitNetCents, vatPercent);
}

function failureOfRequest(error: unknown): PartPriceResolutionFailure {
  if (error instanceof PartPriceResolutionFailure) return error;
  const status = (error as { status?: unknown } | null)?.status;
  return new PartPriceResolutionFailure(status === 403 ? 'forbidden' : 'request_failed');
}

function withTimeout<T>(request: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new PartPriceResolutionFailure('timeout')), RESOLVE_TIMEOUT_MS);
    request.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(failureOfRequest(error));
      },
    );
  });
}

function usablePrice(entry: ResolvedPartPrice | undefined, requested: string): ResolvedPartLinePrice {
  if (!entry) throw new PartPriceResolutionFailure('invalid_response', [requested]);
  if (entry.status === 'part_not_found') throw new PartPriceResolutionFailure('part_not_found', [requested]);
  if (entry.status === 'unavailable') throw new PartPriceResolutionFailure('unavailable', [requested]);
  if (
    entry.status !== 'resolved' ||
    typeof entry.unitNetCents !== 'number' ||
    !Number.isFinite(entry.unitNetCents) ||
    entry.unitNetCents < 0 ||
    !RESOLVED_SOURCES.includes(entry.source)
  ) {
    throw new PartPriceResolutionFailure('invalid_response', [requested]);
  }
  return { partId: requested, unitNetCents: entry.unitNetCents, source: entry.source };
}

export interface WorkOrderPartPricing {
  /** The caller holds both permissions the booking-context resolve needs, in a signed-in, unlocked session. */
  canResolve: boolean;
  /**
   * Prices the parts for this work order. Resolves with exactly one price per distinct id, or rejects:
   * `PartPriceResolutionFailure` (show it), `OwnerChangedError` / `LateResultError` (the session or a permission changed:
   * show nothing, see `isSessionCancel`).
   */
  resolve: (partIds: readonly string[]) => Promise<Map<string, ResolvedPartLinePrice>>;
}

export function useWorkOrderPartPricing(workOrderId: string | null | undefined): WorkOrderPartPricing {
  const access = usePricingAccess();
  const mayReadWorkOrders = useAuthStore((state) => state.hasPermission(WORK_ORDER_READ_PERMISSION));
  const bookingId = workOrderId ?? '';
  const canResolve = access.canRead && mayReadWorkOrders && bookingId !== '';

  const resolve = useCallback(
    async (partIds: readonly string[]): Promise<Map<string, ResolvedPartLinePrice>> => {
      const distinct = [...new Set(partIds)];
      const result = new Map<string, ResolvedPartLinePrice>();
      if (distinct.length === 0) return result;
      if (bookingId === '') throw new PartPriceResolutionFailure('forbidden', distinct);

      for (let start = 0; start < distinct.length; start += MAX_RESOLVE_PARTS_PER_REQUEST) {
        const chunk = distinct.slice(start, start + MAX_RESOLVE_PARTS_PER_REQUEST);
        let response: ResolvePartPricesResponse;
        try {
          response = await runOwned(access, PART_RESOLVE_PERMISSIONS, () =>
            withTimeout(resolvePartPrices({ partIds: chunk, context: { bookingId } })),
          );
        } catch (error) {
          // Refused before anything was sent: a permission was lost since this render.
          if (error instanceof PricingAccessError) throw new PartPriceResolutionFailure('forbidden', chunk);
          throw error;
        }
        const prices = Array.isArray(response?.prices) ? response.prices : null;
        if (prices === null) throw new PartPriceResolutionFailure('invalid_response', chunk);
        const byId = new Map(prices.map((entry) => [String(entry.partId).toLowerCase(), entry]));
        for (const requested of chunk) result.set(requested, usablePrice(byId.get(requested.toLowerCase()), requested));
      }
      return result;
    },
    [access, bookingId],
  );

  return { canResolve, resolve };
}
